// LearnCore Frontend - 阅读材料学习页面

import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import Loading from '../components/Loading';
import type { ReadingPassageWithDetails } from '../types';
import ttsService, { TTSService } from '../services/ttsService';
import {
  DocumentTextIcon,
  ArrowLeftIcon,
  PencilIcon,
  EyeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

type DetailType = 'vocabularies' | 'grammar' | 'collocations' | 'sentences';

export default function StudyPassagePage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const [passage, setPassage] = useState<ReadingPassageWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailType>('vocabularies');
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [highlightedSentence, setHighlightedSentence] = useState<string | null>(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const ttsSupported = TTSService.isSupported();

  // 文本选择提取相关状态
  const [selectedText, setSelectedText] = useState('');
  const [showExtractButton, setShowExtractButton] = useState(false);
  const [extractButtonPosition, setExtractButtonPosition] = useState({ top: 0, left: 0 });
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractingTexts, setExtractingTexts] = useState<string[]>([]); // 正在提取的文本列表
  const [extractingRanges, setExtractingRanges] = useState<Array<{ start: number; end: number; text: string }>>([]); // 正在提取的Range位置

  const contentRef = useRef<HTMLDivElement>(null);
  const detailPanelRef = useRef<HTMLDivElement>(null);
  const savedScrollPosition = useRef<number>(0);

  useEffect(() => {
    // 加载TTS设置，确保使用用户中心配置的音色
    ttsService.loadSettings();
  }, []);

  useEffect(() => {
    if (id) {
      loadPassage();
    }
  }, [id]);

  // 轮询机制：如果有正在提取的文本，每5秒刷新一次
  useEffect(() => {
    if (extractingTexts.length > 0) {
      console.log('🔄 启动轮询，正在提取的文本数量:', extractingTexts.length);
      const interval = setInterval(() => {
        console.log('🔄 执行轮询刷新...');
        loadPassage();
      }, 5000); // 每5秒刷新一次

      return () => {
        console.log('🛑 停止轮询');
        clearInterval(interval);
      };
    }
  }, [extractingTexts, id]);

  // 添加文档点击监听
  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  const loadPassage = async () => {
    try {
      // 保存当前滚动位置
      if (contentRef.current) {
        savedScrollPosition.current = contentRef.current.scrollTop;
        console.log('💾 保存滚动位置:', savedScrollPosition.current);
      }

      setLoading(true);
      setError(null);
      const response = await apiClient.getReadingPassage(id!);
      setPassage(response.passage);
      
      // 如果材料不在处理中，清空正在提取的文本列表和范围
      if (response.passage.processing_status !== 'processing' && response.passage.processing_status !== 'pending') {
        setExtractingTexts([]);
        setExtractingRanges([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '加载阅读材料失败');
    } finally {
      setLoading(false);
      
      // 恢复滚动位置
      requestAnimationFrame(() => {
        if (contentRef.current && savedScrollPosition.current > 0) {
          contentRef.current.scrollTop = savedScrollPosition.current;
          console.log('📜 恢复滚动位置:', savedScrollPosition.current);
        }
      });
    }
  };

  // 朗读文本
  const handleSpeak = async (text: string) => {
    try {
      await ttsService.speak(text);
    } catch (err) {
      console.error('TTS播放失败', err);
    }
  };

  // 朗读词汇（词 + 例句）
  const handleSpeakVocabulary = async (word: string, example?: string) => {
    const parts: string[] = [];
    if (word && word.trim()) parts.push(word);
    if (example && example.trim()) parts.push(example);
    if (parts.length === 0) return;
    await handleSpeak(parts.join('. '));
  };

  // 高亮词汇和搭配 - 优化版本，支持多重叠加高亮
  const highlightText = (text: string) => {
    if (!passage) return text;

    // 收集所有高亮区间信息
    interface HighlightInterval {
      start: number;
      end: number;
      type: 'vocab' | 'collocation' | 'sentence' | 'extracting';
      identifier: string;
    }

    const intervals: HighlightInterval[] = [];

    // 收集正在提取的文本区间（使用保存的范围信息）
    if (extractingRanges.length > 0) {
      console.log('🎨 高亮渲染 - 正在提取的范围数量:', extractingRanges.length);
      
      // 计算当前段落在original_text中的起始位置
      const paragraphStart = passage.original_text.indexOf(text);
      
      if (paragraphStart !== -1) {
        const paragraphEnd = paragraphStart + text.length;
        
        extractingRanges.forEach(range => {
          // 检查range是否与当前段落重叠
          if (range.start < paragraphEnd && range.end > paragraphStart) {
            // 计算相对于段落的位置
            const relativeStart = Math.max(0, range.start - paragraphStart);
            const relativeEnd = Math.min(text.length, range.end - paragraphStart);
            
            if (relativeStart < relativeEnd) {
              console.log('✅ 找到提取区间:', {
                text: text.substring(relativeStart, relativeEnd).substring(0, 50) + '...',
                start: relativeStart,
                end: relativeEnd
              });
              
              intervals.push({
                start: relativeStart,
                end: relativeEnd,
                type: 'extracting',
                identifier: range.text
              });
            }
          }
        });
      }
    }

    // 收集长难句区间
    passage.complex_sentences.forEach(sentence => {
      if (sentence.sentence) {
        const regex = new RegExp(escapeRegExp(sentence.sentence), 'gi');
        let match;
        while ((match = regex.exec(text)) !== null) {
          intervals.push({
            start: match.index,
            end: match.index + match[0].length,
            type: 'sentence',
            identifier: sentence.sentence
          });
        }
      }
    });

    // 收集固定搭配区间
    passage.collocations.forEach(collocation => {
      if (collocation.collocation_occurrences && collocation.collocation_occurrences.length > 0) {
        collocation.collocation_occurrences.forEach(occurrence => {
          const regex = new RegExp(escapeRegExp(occurrence), 'gi');
          let match;
          while ((match = regex.exec(text)) !== null) {
            intervals.push({
              start: match.index,
              end: match.index + match[0].length,
              type: 'collocation',
              identifier: collocation.collocation
            });
          }
        });
      } else {
        const pattern = createFlexiblePattern(collocation.collocation);
        const regex = new RegExp(pattern, 'gi');
        let match;
        while ((match = regex.exec(text)) !== null) {
          intervals.push({
            start: match.index,
            end: match.index + match[0].length,
            type: 'collocation',
            identifier: collocation.collocation
          });
        }
      }
    });

    // 收集词汇区间
    passage.vocabularies.forEach(vocab => {
      if (vocab.word_occurrences && vocab.word_occurrences.length > 0) {
        vocab.word_occurrences.forEach(occurrence => {
          const regex = new RegExp(`\\b${escapeRegExp(occurrence)}\\b`, 'gi');
          let match;
          while ((match = regex.exec(text)) !== null) {
            intervals.push({
              start: match.index,
              end: match.index + match[0].length,
              type: 'vocab',
              identifier: vocab.word
            });
          }
        });
      } else {
        const pattern = createFlexiblePattern(vocab.word);
        const regex = new RegExp(pattern, 'gi');
        let match;
        while ((match = regex.exec(text)) !== null) {
          intervals.push({
            start: match.index,
            end: match.index + match[0].length,
            type: 'vocab',
            identifier: vocab.word
          });
        }
      }
    });

    // 如果没有高亮，直接返回原文
    if (intervals.length === 0) {
      return text;
    }

    // 按位置排序
    intervals.sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      return b.end - a.end; // 相同起点，长的在前
    });

    // 构建嵌套的高亮HTML
    return buildNestedHighlights(text, intervals, 0, text.length);
  };

  // 转义HTML特殊字符（用于属性值）
  const escapeHtml = (str: string): string => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  };

  // 递归构建嵌套高亮
  const buildNestedHighlights = (
    text: string,
    intervals: Array<{ start: number; end: number; type: string; identifier: string }>,
    rangeStart: number,
    rangeEnd: number
  ): string => {
    // 找出当前范围内的所有区间
    const currentIntervals = intervals.filter(
      interval => interval.start >= rangeStart && interval.end <= rangeEnd
    );

    if (currentIntervals.length === 0) {
      return text.substring(rangeStart, rangeEnd);
    }

    // 找出最外层的区间（最长的包含区间）
    const outerInterval = currentIntervals.reduce((longest, current) => {
      const currentLength = current.end - current.start;
      const longestLength = longest.end - longest.start;
      if (currentLength > longestLength) return current;
      if (currentLength === longestLength && current.start < longest.start) return current;
      return longest;
    }, currentIntervals[0]);

    let result = '';
    
    // 处理外层区间之前的文本
    if (outerInterval.start > rangeStart) {
      result += buildNestedHighlights(text, currentIntervals, rangeStart, outerInterval.start);
    }

    // 包裹外层区间
    // 转义 identifier 中的特殊字符用于 HTML 属性
    const escapedIdentifier = escapeHtml(outerInterval.identifier);
    
    const className = outerInterval.type === 'vocab' ? 'highlight-vocab' :
                     outerInterval.type === 'collocation' ? 'highlight-collocation' :
                     outerInterval.type === 'sentence' ? 'highlight-sentence' :
                     'highlight-extracting';
    const dataAttr = outerInterval.type === 'vocab' ? `data-word="${escapedIdentifier}"` :
                     outerInterval.type === 'collocation' ? `data-collocation="${escapedIdentifier}"` :
                     outerInterval.type === 'sentence' ? `data-sentence="${escapedIdentifier}"` :
                     `data-extracting="${escapedIdentifier}"`;

    // 递归处理内部的嵌套高亮
    const innerContent = buildNestedHighlights(
      text,
      currentIntervals.filter(i => i !== outerInterval),
      outerInterval.start,
      outerInterval.end
    );

    result += `<span class="${className}" ${dataAttr}>${innerContent}</span>`;

    // 处理外层区间之后的文本
    if (outerInterval.end < rangeEnd) {
      result += buildNestedHighlights(text, currentIntervals, outerInterval.end, rangeEnd);
    }

    return result;
  };

  // 创建灵活的匹配模式，支持词形变化
  const createFlexiblePattern = (word: string): string => {
    // 处理常见的词形变化
    // 例如：dispose of -> disposes? of|disposing of|disposed of
    const escaped = escapeRegExp(word);
    
    // 简单的词形变化处理
    // 匹配可能的 -s, -es, -ed, -ing 等变化
    const flexiblePattern = escaped
      .replace(/\b(\w+?)e?\b/g, (match, stem) => {
        if (stem.length < 3) return match; // 太短的词不处理
        // 生成可能的变体：原形、-s、-es、-ed、-ing
        return `\\b${stem}(?:e|es|ed|ing)?\\b`;
      });
    
    return flexiblePattern;
  };

  // 在详情面板内滚动到指定元素
  const scrollToElementInPanel = (element: Element) => {
    console.log('🔍 scrollToElementInPanel 开始执行');
    
    if (!detailPanelRef.current) {
      console.log('❌ detailPanelRef.current 为空');
      return;
    }
    
    const panel = detailPanelRef.current;
    const elementRect = element.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    
    console.log('📊 元素信息:', {
      elementText: element.textContent?.substring(0, 50) + '...',
      elementTop: elementRect.top,
      elementBottom: elementRect.bottom,
      elementHeight: elementRect.height
    });
    
    console.log('📊 面板信息:', {
      panelTop: panelRect.top,
      panelBottom: panelRect.bottom,
      panelHeight: panelRect.height,
      panelScrollTop: panel.scrollTop,
      panelScrollHeight: panel.scrollHeight
    });

    // 计算元素相对于面板的位置
    const elementTop = elementRect.top - panelRect.top + panel.scrollTop;
    const elementTopInPanel = elementRect.top - panelRect.top;
    const elementCenter = elementTop + elementRect.height / 2;
    const panelCenter = panel.clientHeight / 2;
    
    // 将元素滚动到面板中间，加入边界夹紧
    let targetScrollTop = Math.round(elementCenter - panelCenter);
    if (targetScrollTop < 0) targetScrollTop = 0;
    const maxScrollTop = panel.scrollHeight - panel.clientHeight;
    if (targetScrollTop > maxScrollTop) targetScrollTop = maxScrollTop;
    
    // 如果由于夹紧导致 targetScrollTop == current 且元素在上半区，进行微调：滚到元素上方留 100px
    if (Math.abs(targetScrollTop - panel.scrollTop) < 1 && elementTopInPanel > 0 && elementTopInPanel < panelCenter) {
      const adjusted = Math.max(0, Math.min(maxScrollTop, elementTop - 100));
      console.log('🛠️ 夹紧导致无滚动，应用上方留白微调', { adjusted });
      targetScrollTop = adjusted;
    }
    
    console.log('🎯 滚动计算:', {
      elementTop,
      elementTopInPanel,
      elementCenter,
      panelCenter,
      targetScrollTop,
      maxScrollTop,
      currentScrollTop: panel.scrollTop
    });
    
    // 平滑滚动
    const beforeScrollTop = panel.scrollTop;
    panel.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth'
    });
    
    console.log('🚀 滚动命令已执行', { beforeScrollTop });
    
    // 使用 rAF 检查是否产生了滚动变化，若无变化则触发回退方案
    requestAnimationFrame(() => {
      const afterScrollTop = panel.scrollTop;
      console.log('🧪 滚动结果检查', { afterScrollTop });
      
      if (Math.abs(afterScrollTop - beforeScrollTop) < 1) {
        // 二次尝试：使用新的目标（强制上方留白）
        const retryTarget = Math.max(0, Math.min(maxScrollTop, elementTop - 100));
        console.log('🔁 二次内部滚动尝试', { retryTarget });
        panel.scrollTo({ top: retryTarget, behavior: 'smooth' });
        
        requestAnimationFrame(() => {
          const afterRetryScrollTop = panel.scrollTop;
          console.log('🧪 二次滚动结果检查', { afterRetryScrollTop });
          if (Math.abs(afterRetryScrollTop - beforeScrollTop) < 1) {
            console.log('⚠️ 内部滚动未生效，触发回退方案 scrollIntoView');
            try {
              (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            } catch (e) {
              console.log('❗ scrollIntoView 回退失败', e);
            }
          }
        });
      }
    });
  };

  // 安全地查找具有指定属性值的元素
  const findElementByDataAttribute = (attributeName: string, attributeValue: string): Element | null => {
    console.log('🔎 查找元素:', { attributeName, attributeValue });
    
    const elements = document.querySelectorAll(`[${attributeName}]`);
    console.log('📋 找到的所有元素数量:', elements.length);
    
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      const attrValue = element.getAttribute(attributeName);
      console.log(`🔍 检查元素 ${i}:`, {
        attrValue,
        matches: attrValue === attributeValue,
        elementText: element.textContent?.substring(0, 30) + '...'
      });
      
      if (attrValue === attributeValue) {
        console.log('✅ 找到匹配的元素');
        return element;
      }
    }
    
    console.log('❌ 未找到匹配的元素');
    return null;
  };

  // 处理点击高亮内容
  const handleHighlightClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const word = target.getAttribute('data-word');
    const collocation = target.getAttribute('data-collocation');
    const sentence = target.getAttribute('data-sentence');

    console.log('🖱️ 点击高亮内容:', { word, collocation, sentence });

    if (word) {
      console.log('📝 处理词汇点击:', word);
      setSelectedWord(word);
      setActiveTab('vocabularies');
      // 滚动到对应词汇 - 增加延迟确保DOM更新完成
      setTimeout(() => {
        console.log('⏰ 延迟执行词汇滚动');
        const vocabElement = findElementByDataAttribute('data-vocab-word', word);
        if (vocabElement) {
          scrollToElementInPanel(vocabElement);
        } else {
          console.log('❌ 未找到词汇元素');
        }
      }, 150);
    } else if (collocation) {
      console.log('📝 处理搭配点击:', collocation);
      setSelectedWord(collocation);
      setActiveTab('collocations');
      // 滚动到对应搭配 - 增加延迟确保DOM更新完成
      setTimeout(() => {
        console.log('⏰ 延迟执行搭配滚动');
        const collocationElement = findElementByDataAttribute('data-collocation', collocation);
        if (collocationElement) {
          scrollToElementInPanel(collocationElement);
        } else {
          console.log('❌ 未找到搭配元素');
        }
      }, 150);
    } else if (sentence) {
      console.log('📝 处理句子点击:', sentence);
      setHighlightedSentence(sentence);
      setActiveTab('sentences');
      // 滚动到对应句子 - 增加延迟确保DOM更新完成
      setTimeout(() => {
        console.log('⏰ 延迟执行句子滚动');
        const sentenceElement = findElementByDataAttribute('data-sentence', sentence);
        if (sentenceElement) {
          scrollToElementInPanel(sentenceElement);
        } else {
          console.log('❌ 未找到句子元素');
        }
      }, 150);
    }
  };

  // 转义正则表达式特殊字符
  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // 文本选择处理
  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    if (text && text.length > 10) { // 至少10个字符
      setSelectedText(text);
      // 计算浮动按钮位置（选区末尾）
      const range = selection?.getRangeAt(0);
      if (range) {
        const rect = range.getBoundingClientRect();
        setExtractButtonPosition({ top: rect.bottom + 5, left: rect.left });
        setShowExtractButton(true);
      }
    } else {
      setShowExtractButton(false);
    }
  };

  // 提取处理
  const handleExtractSelection = async () => {
    if (!passage || !selectedText) return;
    
    setIsExtracting(true);
    
    // 保存当前选中的文本和Selection对象
    const textToExtract = selectedText.trim();
    const selection = window.getSelection();
    
    // 获取选区的绝对位置（相对于整个passage的original_text）
    let selectionInfo: { start: number; end: number; text: string } | null = null;
    
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      
      // 尝试找到包含文本的元素
      let element: Element | null = null;
      if (container.nodeType === Node.ELEMENT_NODE) {
        element = container as Element;
      } else if (container.parentElement) {
        element = container.parentElement;
      }
      
      // 获取完整的文本内容来计算位置
      if (element) {
        const fullText = passage.original_text;
        const selectedText = range.toString();
        const startOffset = fullText.indexOf(selectedText);
        
        if (startOffset !== -1) {
          selectionInfo = {
            start: startOffset,
            end: startOffset + selectedText.length,
            text: selectedText
          };
          console.log('📍 选区位置:', selectionInfo);
        }
      }
    }
    
    // 先隐藏按钮和清空选择
    setShowExtractButton(false);
    setSelectedText('');
    
    // 添加到正在提取的文本列表和Range列表
    setExtractingTexts(prev => {
      console.log('📝 添加到提取列表:', textToExtract);
      const newList = [...prev, textToExtract];
      console.log('📋 当前提取列表:', newList);
      return newList;
    });
    
    if (selectionInfo) {
      setExtractingRanges(prev => [...prev, selectionInfo!]);
    }
    
    try {
      await apiClient.extractSelectedText(passage.id, textToExtract);
      console.log('✅ 提取请求已提交');
      
      // 不立即刷新页面，让轮询机制自动更新
      console.log('⏱️ 等待轮询机制自动更新状态...');
    } catch (error: any) {
      console.error('提取选中文本失败:', error);
      alert(error.response?.data?.error || '提取失败，请稍后重试');
      // 失败时从提取列表中移除
      setExtractingTexts(prev => prev.filter(t => t !== textToExtract));
      if (selectionInfo) {
        setExtractingRanges(prev => prev.filter(r => r.text !== textToExtract));
      }
    } finally {
      setIsExtracting(false);
    }
  };

  // 点击其他区域隐藏提取按钮
  const handleDocumentClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.extract-button-container') && !target.closest('.prose')) {
      setShowExtractButton(false);
    }
  };

  // 分割段落并混排原文与译文
  const renderMixedContent = () => {
    if (!passage) return null;

    // 按段落分割原文和译文（以双换行或单换行分割）
    const originalParagraphs = passage.original_text.split(/\n\n+/).filter(p => p.trim());
    const translationParagraphs = passage.translation
      ? passage.translation.split(/\n\n+/).filter(p => p.trim())
      : [];

    return (
      <div className="space-y-6">
        {originalParagraphs.map((paragraph, index) => (
          <div key={`paragraph-${index}-${extractingRanges.length}`} className="border-l-4 border-primary-200 pl-4">
            {/* 英文原文段落 */}
            {ttsSupported && (
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => handleSpeak(paragraph)}
                  className="text-primary-600 hover:text-primary-700 text-sm flex items-center"
                  title="朗读原文"
                >
                  <i className="fa-solid fa-volume-high mr-1" />
                  朗读
                </button>
              </div>
            )}
            <div
              className="prose prose-lg max-w-none leading-relaxed mb-3"
              onClick={handleHighlightClick}
              onMouseUp={handleTextSelection}
              dangerouslySetInnerHTML={{
                __html: highlightText(paragraph)
              }}
            />
            {/* 中文译文段落 - 仅在显示译文时显示 */}
            {showTranslation && translationParagraphs[index] && (
              <div className="text-gray-600 leading-relaxed text-base bg-gray-50 p-3 rounded-md">
                {translationParagraphs[index]}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading size="lg" text="加载阅读材料..." />
      </div>
    );
  }

  if (error || !passage) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">加载失败</h2>
          <p className="text-gray-600 mb-6">{error || '阅读材料不存在'}</p>
          <Link to="/passages" className="btn-primary">
            返回阅读材料列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-screen flex flex-col">
      {/* 头部导航 */}
      <div className="mb-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/passages"
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              返回
            </Link>

            <div>
              <h1 className="text-2xl font-bold text-gray-900">{passage.title}</h1>
              
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {user && passage.user_id === user.id && (
              <Link
                to={`/passages/${passage.id}/edit`}
                className="btn-outline flex items-center"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                编辑
              </Link>
            )}

            <button
              onClick={() => setShowTranslation(!showTranslation)}
              className="btn-outline flex items-center"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              {showTranslation ? '隐藏译文' : '显示译文'}
            </button>
          </div>
        </div>
      </div>

       <div className="flex-1 max-h-[calc(100vh-12rem)]">
         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-full">
         {/* 左侧主内容区域 */}
         <div className="lg:col-span-2 flex flex-col">
           <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col h-full max-h-[calc(100vh-12rem)]">
             <div className="flex items-center justify-between mb-4 flex-shrink-0">
               <h2 className="text-lg font-medium text-gray-900 flex items-center">
                 <DocumentTextIcon className="h-5 w-5 mr-2 text-primary-600" />
                 原文与译文
               </h2>
               <span className="text-sm text-gray-500">
                 点击词汇、搭配或句子查看详情
               </span>
             </div>

             <div ref={contentRef} className="flex-1 overflow-y-auto">
               {renderMixedContent()}
             </div>
           </div>
         </div>

         {/* 右侧详情面板 */}
         <div className="lg:col-span-1 flex flex-col">
           <div className="bg-white rounded-lg border border-gray-200 flex flex-col h-full max-h-[calc(100vh-12rem)]">
            {/* 标签页导航 */}
            <div className="border-b border-gray-200 flex-shrink-0">
              <nav className="flex">
                {[
                  { key: 'vocabularies', label: `词汇 (${passage.vocabularies.length})` },
                  { key: 'grammar', label: `语法 (${passage.grammar_points.length})` },
                  { key: 'collocations', label: `搭配 (${passage.collocations.length})` },
                  { key: 'sentences', label: `长难句 (${passage.complex_sentences.length})` }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as DetailType)}
                    className={`flex-1 px-3 py-3 text-sm font-medium border-b-2 ${
                      activeTab === tab.key
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <span className={
                      tab.key === 'vocabularies' ? 'highlight-vocab' :
                      tab.key === 'collocations' ? 'highlight-collocation' :
                      tab.key === 'sentences' ? 'highlight-sentence' : ''
                    }>
                      {tab.key === 'vocabularies' ? '词汇' :
                       tab.key === 'collocations' ? '搭配' :
                       tab.key === 'sentences' ? '长难句' : '语法'}
                    </span>
                    {` (${tab.key === 'vocabularies' ? passage.vocabularies.length :
                       tab.key === 'collocations' ? passage.collocations.length :
                       tab.key === 'sentences' ? passage.complex_sentences.length :
                       passage.grammar_points.length})`}
                  </button>
                ))}
              </nav>
            </div>

            {/* 详情内容 */}
            <div ref={detailPanelRef} className="flex-1 p-6 overflow-y-auto">
              {/* 词汇列表 */}
              {activeTab === 'vocabularies' && (
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-3">重点词汇</h3>
                  {passage.vocabularies.length === 0 ? (
                    <p className="text-gray-500 text-sm">暂无词汇</p>
                  ) : (
                    <div className="space-y-3">
                      {passage.vocabularies.map((vocab) => (
                        <div
                          key={vocab.id}
                          className={`p-3 rounded-lg border ${
                            selectedWord === vocab.word ? 'border-primary-300 bg-primary-50' : 'border-gray-200'
                          }`}
                          data-vocab-word={vocab.word}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-medium text-gray-900">
                              {vocab.word}
                            </div>
                            {ttsSupported && (
                              <button
                                onClick={() => handleSpeakVocabulary(vocab.word, vocab.example_sentence)}
                                className="text-primary-600 hover:text-primary-700 text-xs flex items-center"
                                title="朗读词汇"
                              >
                                <i className="fa-solid fa-volume-high mr-1" />
                                朗读
                              </button>
                            )}
                          </div>
                          {vocab.example_sentence && (
                            <div className="text-sm text-gray-600 mb-2 italic">
                              "{vocab.example_sentence}"
                            </div>
                          )}
                          <div className="text-sm text-gray-700">
                            {vocab.explanation}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 语法考点 */}
              {activeTab === 'grammar' && (
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-3">语法考点</h3>
                  {passage.grammar_points.length === 0 ? (
                    <p className="text-gray-500 text-sm">暂无语法考点</p>
                  ) : (
                    <div className="space-y-3">
                      {passage.grammar_points.map((grammar) => (
                        <div key={grammar.id} className="p-3 rounded-lg border border-gray-200">
                          <div className="font-medium text-gray-900 mb-1">
                            {grammar.point_name}
                          </div>
                          {grammar.example_text && (
                            <div className="text-sm text-gray-600 mb-2 italic">
                              "{grammar.example_text}"
                            </div>
                          )}
                          <div className="text-sm text-gray-700">
                            {grammar.explanation}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 固定搭配 */}
              {activeTab === 'collocations' && (
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-3">固定搭配</h3>
                  {passage.collocations.length === 0 ? (
                    <p className="text-gray-500 text-sm">暂无固定搭配</p>
                  ) : (
                    <div className="space-y-3">
                      {passage.collocations.map((collocation) => (
                        <div
                          key={collocation.id}
                          className={`p-3 rounded-lg border ${
                            selectedWord === collocation.collocation ? 'border-primary-300 bg-primary-50' : 'border-gray-200'
                          }`}
                          data-collocation={collocation.collocation}
                        >
                          <div className="font-medium text-gray-900 mb-1">
                            {collocation.collocation}
                          </div>
                          <div className="text-sm text-gray-700 mb-1">
                            <span className="font-medium">含义：</span>{collocation.meaning}
                          </div>
                          {collocation.usage_note && (
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">用法：</span>{collocation.usage_note}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 长难句分析 */}
              {activeTab === 'sentences' && (
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-3">长难句分析</h3>
                  {passage.complex_sentences.length === 0 ? (
                    <p className="text-gray-500 text-sm">暂无长难句分析</p>
                  ) : (
                    <div className="space-y-4">
                      {passage.complex_sentences.map((sentence) => (
                        <div
                          key={sentence.id}
                          className={`p-3 rounded-lg border ${
                            highlightedSentence === sentence.sentence ? 'border-primary-300 bg-primary-50' : 'border-gray-200'
                          }`}
                          data-sentence={sentence.sentence}
                        >
                          <div className="text-sm text-gray-900 mb-2 font-medium">
                            英文句子：
                          </div>
                          <div className="text-sm text-gray-700 mb-3 italic">
                            "{sentence.sentence}"
                          </div>

                          <div className="text-sm text-gray-900 mb-2 font-medium">
                            结构分析：
                          </div>
                          <div className="text-sm text-gray-700 mb-3">
                            {sentence.structure_analysis}
                          </div>

                          <div className="text-sm text-gray-900 mb-2 font-medium">
                            中文翻译：
                          </div>
                          <div className="text-sm text-gray-700">
                            {sentence.translation}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* 浮动提取按钮 */}
      {showExtractButton && (
        <div
          className="extract-button-container fixed z-50"
          style={{
            top: `${extractButtonPosition.top}px`,
            left: `${extractButtonPosition.left}px`,
          }}
        >
          <button
            onClick={handleExtractSelection}
            disabled={isExtracting}
            className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 transition-colors duration-200"
          >
            {isExtracting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span>提取中...</span>
              </>
            ) : (
              <>
                <SparklesIcon className="h-4 w-4" />
                <span>提取学习内容</span>
              </>
            )}
          </button>
        </div>
      )}

    </div>
  );
}
